import { styled } from '@linaria/react';
import { Divider, Group, TextInput } from '@mantine/core';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import type { ReactNode } from 'react';
import type { IntegrationType } from '../../../../api/integrations-client/integrations-client.type';
import { baseWhite, redBase, skyBase, skyDark } from '../../../../styles/design-tokens';
import { Button } from '../../../../ui-library/button/button';
import { Icon } from '../../../../ui-library/icon/icon';
import { Text } from '../../../../ui-library/typography/typography';
import { DataManagementDropShadowContainer } from '../data-management-header';
import { formatDatetimeToLocal, getDataSourceIcon, getDataSourceLabel } from '../data-management.helpers';
import { DeleteConfirmationModal } from '../delete-confirmation-modal';
import { Table, TableColumn } from '../table';
import { useDeleteBoardSetting, useEditBoardSetting } from './board-settings-client.hooks';
import type { BoardSetting, EditBoardSettingPayload } from './board-settings-client.type';
import { BoardSettingsMetadata } from './board-settings-metadata';
import {
  computeNameIsDirty,
  getProjectGroups,
  UNKNOWN_PROJECT_NAME,
  UNKNOWN_SUBPROJECT_NAME,
} from './board-settings.helpers';
import type { ExtendedSubproject } from './board-settings.type';
import type { PendingSubprojectChanges } from './edit-board-settings-panel';

type TableRow = {
  key: string;
  id: string;
  name: string;
  projectName: string;
  dataSource: ReactNode | null;
  lastUpdated: string | null;
  isProject: boolean;
  parentId?: string;
};

type BoardSettingsPanelProps = {
  subprojects: ExtendedSubproject[];
  isEditing: boolean;
  setIsEditing: (isEditing: boolean) => void;
  selectedSubprojectId: string | null;
  setSelectedSubprojectId: (id: string | null) => void;
  selectedBoardSetting: BoardSetting | null;
  pendingSubprojectChanges: PendingSubprojectChanges;
  setPendingSubprojectChanges: (changes: PendingSubprojectChanges) => void;
  addSubprojectsToBoardSetting: (
    boardSettingId: string,
    subprojectIds: string[],
    organizationId: string,
  ) => Promise<void>;
  removeSubprojectsFromBoardSetting: (
    boardSettingId: string,
    subprojectIds: string[],
    organizationId: string,
  ) => Promise<void>;
  isAdding: boolean;
  isRemoving: boolean;
};

export function BoardSettingsPanel({
  subprojects,
  isEditing,
  setIsEditing,
  selectedSubprojectId,
  setSelectedSubprojectId,
  selectedBoardSetting,
  pendingSubprojectChanges,
  setPendingSubprojectChanges,
  addSubprojectsToBoardSetting,
  removeSubprojectsFromBoardSetting,
  isAdding,
  isRemoving,
}: BoardSettingsPanelProps) {
  const [search, setSearch] = useState('');
  const [isEditingName, setIsEditingName] = useState(false);
  const [editedName, setEditedName] = useState(selectedBoardSetting?.name || '');
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [highlightedRow, setHighlightedRow] = useState<string | null>(null);
  const [pendingChanges, setPendingChanges] = useState<Partial<EditBoardSettingPayload>>({});
  const queryClient = useQueryClient();

  const nameIsDirty = computeNameIsDirty(editedName, selectedBoardSetting?.name);

  // When the user exits edit mode without saving, discard any in-progress name draft.
  useEffect(() => {
    if (!isEditing) {
      setIsEditingName(false);
      setEditedName(selectedBoardSetting?.name || '');
    }
  }, [isEditing, selectedBoardSetting?.name]);

  // When the selected board changes, reset the draft so it doesn't carry over.
  useEffect(() => {
    setIsEditingName(false);
    setEditedName(selectedBoardSetting?.name || '');
  }, [selectedBoardSetting]);
  const { editBoardSetting, isEditing: isEditingBoardSetting } = useEditBoardSetting();
  const { deleteBoardSetting, isDeleting } = useDeleteBoardSetting();

  const handleNameEdit = () => {
    setIsEditingName(true);
    setEditedName(selectedBoardSetting?.name || '');
  };

  const handleNameSave = () => {
    setIsEditingName(false);
  };

  const handleNameKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleNameSave();
    } else if (e.key === 'Escape') {
      setIsEditingName(false);
      setEditedName(selectedBoardSetting?.name || '');
    }
  };

  const handleSaveChanges = async () => {
    if (!selectedBoardSetting) return;

    const allChanges: Partial<EditBoardSettingPayload> = { ...pendingChanges };

    if (nameIsDirty) {
      allChanges.name = editedName.trim();
      setIsEditingName(false);
    }

    // Execute all save operations
    const saveRequests: (Promise<void> | Promise<BoardSetting>)[] = [];

    // Update board setting metadata
    if (Object.keys(allChanges).length > 0) {
      saveRequests.push(editBoardSetting(selectedBoardSetting.id, allChanges));
    }

    // Add subprojects
    if (pendingSubprojectChanges.toAdd.length > 0) {
      saveRequests.push(
        addSubprojectsToBoardSetting(
          selectedBoardSetting.id,
          pendingSubprojectChanges.toAdd,
          selectedBoardSetting.organization_id,
        ),
      );
    }

    // Remove subprojects
    if (pendingSubprojectChanges.toRemove.length > 0) {
      saveRequests.push(
        removeSubprojectsFromBoardSetting(
          selectedBoardSetting.id,
          pendingSubprojectChanges.toRemove,
          selectedBoardSetting.organization_id,
        ),
      );
    }

    // Wait for all API requests to complete
    if (saveRequests.length > 0) {
      try {
        await Promise.all(saveRequests);

        // Only invalidate queries and clear state on success
        queryClient.invalidateQueries({ queryKey: ['board-settings'] });
        queryClient.invalidateQueries({ queryKey: ['integration-entries'] });

        // Clear pending changes and exit edit mode
        setPendingChanges({});
        setPendingSubprojectChanges({ toAdd: [], toRemove: [] });
        setIsEditing(false);
      } catch (error) {
        // Log error for debugging
        console.error('Failed to save board settings:', error);
      }
    }
  };

  const hasUnsavedChanges =
    Object.keys(pendingChanges).length > 0 ||
    nameIsDirty ||
    pendingSubprojectChanges.toAdd.length > 0 ||
    pendingSubprojectChanges.toRemove.length > 0;

  const isSaving = isEditingBoardSetting || isAdding || isRemoving;

  const columns: TableColumn<TableRow>[] = [
    {
      key: 'name',
      label: 'Project / Board Name',
      width: '40%',
      sortable: true,
      render: (_value: TableRow[keyof TableRow], row: TableRow) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginLeft: row.isProject ? 0 : 12 }}>
          <Text weight={row.isProject ? 'bold' : 'medium'}>{row.name}</Text>
        </div>
      ),
    },
    {
      key: 'dataSource',
      label: 'Data Source',
      width: '30%',
      sortable: true,
      render: (_value: TableRow[keyof TableRow], row: TableRow) => row.dataSource,
    },
    { key: 'lastUpdated', label: 'Last Updated', width: '30%', sortable: true },
  ];

  const tableData: TableRow[] = [];

  const projectGroups = getProjectGroups(
    subprojects.filter((subproject) => subproject.subproject_settings_id === selectedBoardSetting?.id),
  );

  Object.entries(projectGroups).forEach(([projectId, projectSubprojectsMapping]) => {
    const projectName = projectSubprojectsMapping.name;

    tableData.push({
      id: projectId,
      key: projectId,
      name: projectName,
      projectName: projectName,
      isProject: true,
      dataSource: null,
      lastUpdated: null,
    });

    projectSubprojectsMapping.subprojects.forEach((subproject) => {
      const pmTool = selectedBoardSetting?.subprojects?.find((s) => s.id === subproject.id)?.project_pm_tool;

      tableData.push({
        id: subproject.id,
        key: subproject.key,
        name: subproject.name ?? UNKNOWN_SUBPROJECT_NAME,
        projectName: subproject.project_name ?? UNKNOWN_PROJECT_NAME,
        isProject: false,
        parentId: projectId,
        dataSource: pmTool ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <img
              src={getDataSourceIcon(pmTool as IntegrationType)}
              width={20}
              height={20}
              alt={getDataSourceLabel(pmTool as IntegrationType)}
            />
            <Text>{getDataSourceLabel(pmTool as IntegrationType)}</Text>
          </div>
        ) : null,
        lastUpdated: formatDatetimeToLocal(subproject.last_data_fetch),
      });
    });
  });

  const filteredData = tableData.filter((row) => {
    const searchTerm = search.toLowerCase();
    if (!searchTerm) return true;

    if (row.isProject) {
      const hasMatchingSubprojects = tableData.some(
        (subRow) => !subRow.isProject && subRow.parentId === row.id && subRow.name.toLowerCase().includes(searchTerm),
      );
      return row.name.toLowerCase().includes(searchTerm) || hasMatchingSubprojects;
    } else {
      const parentProject = tableData.find((pr) => pr.id === row.parentId);
      return (
        row.name.toLowerCase().includes(searchTerm) ||
        (parentProject && parentProject.name.toLowerCase().includes(searchTerm))
      );
    }
  });

  const handleRowClick = (row: TableRow) => {
    if (!row.isProject) {
      setSelectedSubprojectId(row.key === highlightedRow && selectedSubprojectId ? null : row.id);
      setHighlightedRow(row.key);
      setIsEditing(false);
    }
  };

  return (
    <DataManagementDropShadowContainer>
      {selectedBoardSetting && (
        <>
          <HeaderContainer>
            <Group gap="8px">
              {isEditingName ? (
                <TextInput
                  value={editedName}
                  onChange={(e) => setEditedName(e.currentTarget.value)}
                  onBlur={handleNameSave}
                  onKeyDown={handleNameKeyPress}
                  autoFocus
                  size="md"
                  radius="xl"
                  style={{ width: '300px', height: '24px', color: skyBase }}
                  disabled={isSaving}
                  rightSection={
                    <Icon
                      name="check_circle"
                      variant="outlined"
                      size={20}
                      color={skyDark}
                      style={{ cursor: isSaving ? 'not-allowed' : 'pointer' }}
                      onClick={handleNameSave}
                    />
                  }
                />
              ) : (
                <>
                  <Text weight="bold" size="large">
                    {nameIsDirty ? editedName : selectedBoardSetting.name}
                  </Text>
                  {isEditing && (
                    <Icon
                      name="edit"
                      size={20}
                      color={skyDark}
                      style={{ cursor: 'pointer' }}
                      onClick={handleNameEdit}
                    />
                  )}
                </>
              )}
            </Group>
            <Group>
              <TextInput
                placeholder="Search for Project or Board"
                value={search}
                onChange={(event) => setSearch(event.currentTarget.value)}
                leftSection={<Icon name="search" size={16} color={skyDark} />}
                style={{ width: 250 }}
                radius="xl"
                size="xs"
              />
              {isEditing ? (
                <>
                  <Button
                    variant="primary"
                    size="xs"
                    radius="xl"
                    leftSection={<Icon name="save" size={16} color={hasUnsavedChanges ? baseWhite : skyDark} />}
                    style={{ padding: '8px 16px', gap: '8px' }}
                    onClick={handleSaveChanges}
                    disabled={!hasUnsavedChanges || isSaving}
                    loading={isSaving}
                  >
                    <Text>Save</Text>
                  </Button>
                  <Button
                    variant="outline"
                    size="xs"
                    radius="xl"
                    leftSection={<Icon name="delete" size={16} color={redBase} />}
                    style={{ padding: '8px 16px', gap: '8px', borderColor: redBase, color: redBase }}
                    onClick={() => setIsDeleteModalOpen(true)}
                    disabled={isSaving}
                  >
                    <Text>Delete</Text>
                  </Button>
                </>
              ) : (
                <Button
                  variant="primary"
                  size="xs"
                  radius="xl"
                  leftSection={<Icon name="edit" variant="filled" size={16} color="white" />}
                  style={{ padding: '8px 16px', gap: '8px' }}
                  onClick={() => {
                    setIsEditing(true);
                    setIsEditingName(false);
                    setSelectedSubprojectId(null);
                  }}
                >
                  <Text>Edit Setting</Text>
                </Button>
              )}
            </Group>
          </HeaderContainer>
          <DeleteConfirmationModal
            isOpen={isDeleteModalOpen}
            onClose={() => setIsDeleteModalOpen(false)}
            onDelete={() => deleteBoardSetting(selectedBoardSetting.id)}
            title="Delete Board Setting"
            itemName={selectedBoardSetting.name}
            isDeleting={isDeleting}
            error={null}
          />
        </>
      )}
      <Divider style={{ marginTop: selectedBoardSetting ? 24 : 54, marginBottom: 24 }} />
      {selectedBoardSetting ? (
        <>
          <BoardSettingsMetadata
            selectedBoardSetting={selectedBoardSetting}
            isEditing={isEditing}
            pendingChanges={pendingChanges}
            onPendingChange={setPendingChanges}
            isSaving={isSaving}
          />
          <Divider style={{ marginTop: 24 }} />
          {filteredData.length > 0 ? (
            <TableContainer>
              <Table
                data={filteredData}
                columns={columns}
                selectedRowId={highlightedRow || undefined}
                onRowClick={handleRowClick}
                getRowId={(row) => row.key}
                stickyHeader
                isChildRow={(row) => !row.isProject}
                getParentId={(row) => row.parentId || ''}
                highlightParentRows
              />
            </TableContainer>
          ) : (
            <EmptyStateContainer>
              <Icon name="add_circle_outlined" size={40} color={skyDark} />
              <Text size="large" color={skyDark}>
                Start adding boards to your settings
              </Text>
            </EmptyStateContainer>
          )}
        </>
      ) : (
        <EmptyStateContainer>
          <Icon name="check_circle_outlined" size={40} color={skyDark} />
          <Text size="large" color={skyDark}>
            No board selected
          </Text>
        </EmptyStateContainer>
      )}
    </DataManagementDropShadowContainer>
  );
}

const HeaderContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
`;

const TableContainer = styled.div`
  flex: 1;
  overflow: auto;
`;

const EmptyStateContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  justify-content: center;
  align-items: center;
  height: 100%;
`;
